import React from 'react';
import { ChevronRight, X } from 'lucide-react';

export interface WorkspaceScopeStat {
  label: string;
  value: number | string;
  color?: string;
}

interface WorkspaceProjectScopeBarProps {
  moduleLabel: string;
  projectName: string;
  statusTone?: 'healthy' | 'risk' | 'critical';
  statusLabel?: string;
  stats?: WorkspaceScopeStat[];
  onDismiss: () => void;
  actions?: React.ReactNode;
}

const TONE_BORDER: Record<NonNullable<WorkspaceProjectScopeBarProps['statusTone']>, string> = {
  healthy: 'border-l-emerald-500',
  risk: 'border-l-amber-500',
  critical: 'border-l-red-500',
};

const TONE_BADGE: Record<NonNullable<WorkspaceProjectScopeBarProps['statusTone']>, string> = {
  healthy: 'bg-emerald-500/15 text-emerald-500',
  risk: 'bg-amber-500/15 text-amber-500',
  critical: 'bg-red-500/15 text-red-500',
};

export const WorkspaceProjectScopeBar: React.FC<WorkspaceProjectScopeBarProps> = ({
  moduleLabel,
  projectName,
  statusTone = 'healthy',
  statusLabel,
  stats = [],
  onDismiss,
  actions,
}) => (
  <div
    className={`flex shrink-0 items-center gap-3 overflow-hidden border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-1.5 border-l-[3px] ${TONE_BORDER[statusTone]}`}
  >
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={onDismiss}
        title="Ver todos — remover filtro de projeto"
        className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
      >
        {moduleLabel}
      </button>
      <ChevronRight size={10} className="shrink-0 text-[var(--text-muted)]" />
      <span className="max-w-[220px] truncate text-[12px] font-semibold text-[var(--text-primary)]">
        {projectName}
      </span>
      {statusLabel && (
        <span className={`ml-1 shrink-0 inline-flex items-center rounded-[3px] px-1.5 py-0.5 text-[9px] font-bold uppercase ${TONE_BADGE[statusTone]}`}>
          {statusLabel}
        </span>
      )}
    </div>

    {stats.length > 0 && (
      <>
        <div className="h-3.5 w-px shrink-0 bg-[var(--border-default)]" />
        <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden text-[11px]">
          {stats.map(({ label, value, color }, index) => (
            <React.Fragment key={label}>
              {index > 0 && <div className="h-3 w-px shrink-0 bg-[var(--border-default)]" />}
              <div className="flex shrink-0 items-center gap-1">
                <span className="text-[var(--text-muted)]">{label}</span>
                <span className={`font-semibold tabular-nums ${color ?? 'text-[var(--text-secondary)]'}`}>{value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </>
    )}

    <div className="ml-auto flex shrink-0 items-center gap-1">
      {actions}
      <button
        type="button"
        onClick={onDismiss}
        title="Fechar contexto do projeto — ver todos"
        className="rounded-sm p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]"
      >
        <X size={13} />
      </button>
    </div>
  </div>
);
